// src/store/slices/taskSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { TaskInterface } from '../../common/types/interfaces'

interface TaskState {
  pool: TaskInterface[]
  my: TaskInterface[]
  selectedTask: TaskInterface | null
  loading: boolean
  refreshing: boolean
  loadingMore: boolean
  hasMorePool: boolean
  hasMoreMy: boolean
  offsetPool: number
  offsetMy: number
  error: string | null
}

const initialState: TaskState = {
  pool: [],
  my: [],
  selectedTask: null,
  loading: false,
  refreshing: false,
  loadingMore: false,
  hasMorePool: true,
  hasMoreMy: true,
  offsetPool: 0,
  offsetMy: 0,
  error: null,
}

const keyOf = (t: TaskInterface) => `${t.clusterId}-${t.id}`

// ❌ сохраняем ссылки на неизменившиеся таски
const mergeTasks = (existing: TaskInterface[], incoming: TaskInterface[]): TaskInterface[] => {
  const existingMap = new Map(existing.map((t) => [keyOf(t), t]))
  return incoming.map((t) => {
    const prev = existingMap.get(keyOf(t))
    if (
      prev &&
      prev.status === t.status &&
      prev.executorId === t.executorId &&
      prev.fee === t.fee &&
      prev.updatedAt === t.updatedAt
    ) {
      return prev
    }
    return t
  })
}

export const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    setPool: (state, action: PayloadAction<TaskInterface[]>) => {
      state.pool = mergeTasks(state.pool, action.payload) // ❌ merge
      state.offsetPool = action.payload.length
      state.hasMorePool = action.payload.length >= 20
    },
    appendPool: (state, action: PayloadAction<TaskInterface[]>) => {
      const existingIds = new Set(state.pool.map(keyOf))
      const newTasks = action.payload.filter((t) => !existingIds.has(keyOf(t)))
      state.pool = [...state.pool, ...newTasks]
      state.offsetPool += action.payload.length
      state.hasMorePool = action.payload.length >= 20
    },
    setMy: (state, action: PayloadAction<TaskInterface[]>) => {
      state.my = mergeTasks(state.my, action.payload) // ❌ merge
      state.offsetMy = action.payload.length
      state.hasMoreMy = action.payload.length >= 20
    },
    appendMy: (state, action: PayloadAction<TaskInterface[]>) => {
      const existingIds = new Set(state.my.map(keyOf))
      const newTasks = action.payload.filter((t) => !existingIds.has(keyOf(t)))
      state.my = [...state.my, ...newTasks]
      state.offsetMy += action.payload.length
      state.hasMoreMy = action.payload.length >= 20
    },
    upsertTask: (state, action: PayloadAction<TaskInterface>) => {
      const task = action.payload
      const key = keyOf(task)

      if (task.status === 0) {
        const exists = state.pool.some((t) => keyOf(t) === key)
        if (!exists) state.pool = [task, ...state.pool]
      } else {
        state.pool = state.pool.filter((t) => keyOf(t) !== key)
      }

      const myIndex = state.my.findIndex((t) => keyOf(t) === key)
      if (myIndex !== -1) {
        state.my[myIndex] = task
      } else if (task.status !== 0) {
        state.my = [task, ...state.my]
      }

      if (
        state.selectedTask &&
        state.selectedTask.id === task.id &&
        state.selectedTask.clusterId === task.clusterId
      ) {
        state.selectedTask = task
      }
    },
    removeTask: (state, action: PayloadAction<{ clusterId: number; id: number }>) => {
      const key = `${action.payload.clusterId}-${action.payload.id}`
      state.pool = state.pool.filter((t) => keyOf(t) !== key)
      state.my = state.my.filter((t) => keyOf(t) !== key)

      if (
        state.selectedTask &&
        state.selectedTask.id === action.payload.id &&
        state.selectedTask.clusterId === action.payload.clusterId
      ) {
        state.selectedTask = null
      }
    },
    setSelectedTask: (state, action: PayloadAction<TaskInterface | null>) => {
      state.selectedTask = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.loadingMore = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    resetTasks: (state) => {
      state.pool = []
      state.my = []
      state.selectedTask = null
      state.offsetPool = 0
      state.offsetMy = 0
      state.hasMorePool = true
      state.hasMoreMy = true
    },
  },
})

export const {
  setPool,
  appendPool,
  setMy,
  appendMy,
  upsertTask,
  removeTask,
  setSelectedTask,
  setLoading,
  setRefreshing,
  setLoadingMore,
  setError,
  resetTasks,
} = taskSlice.actions

export const selectTaskPool = (state: RootState) => state.task.pool
export const selectMyTasks = (state: RootState) => state.task.my
export const selectSelectedTask = (state: RootState) => state.task.selectedTask
export const selectTaskLoading = (state: RootState) => state.task.loading
export const selectTaskRefreshing = (state: RootState) => state.task.refreshing
export const selectTaskLoadingMore = (state: RootState) => state.task.loadingMore
export const selectTaskError = (state: RootState) => state.task.error
export const selectHasMorePool = (state: RootState) => state.task.hasMorePool
export const selectHasMoreMy = (state: RootState) => state.task.hasMoreMy
export const selectOffsetPool = (state: RootState) => state.task.offsetPool
export const selectOffsetMy = (state: RootState) => state.task.offsetMy

export default taskSlice.reducer
